import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'error'

export interface AppNotification {
  id: string
  severity: NotificationSeverity
  title: string
  body?: string
  actionLabel?: string
  actionUrl?: string
  createdAt: string
  read: boolean
}

type NotificationInput = Omit<AppNotification, 'id' | 'createdAt' | 'read'> & {
  id?: string
  createdAt?: string
  read?: boolean
}

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<AppNotification[]>([])
  const panelOpen = ref(false)
  const configModalOpen = ref(false)

  const unreadCount = computed(() => items.value.filter(n => !n.read).length)
  const hasUnread = computed(() => unreadCount.value > 0)
  const sorted = computed(() =>
    [...items.value].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  )

  function genId(): string {
    return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
  }

  function add(input: NotificationInput): string {
    const id = input.id ?? genId()
    items.value.unshift({
      id,
      severity: input.severity,
      title: input.title,
      body: input.body,
      actionLabel: input.actionLabel,
      actionUrl: input.actionUrl,
      createdAt: input.createdAt ?? new Date().toISOString(),
      read: input.read ?? false,
    })
    return id
  }

  function markRead(id: string): void {
    const n = items.value.find(x => x.id === id)
    if (n) n.read = true
  }

  function markAllRead(): void {
    items.value.forEach(n => { n.read = true })
  }

  function dismiss(id: string): void {
    const i = items.value.findIndex(x => x.id === id)
    if (i >= 0) items.value.splice(i, 1)
  }

  function clear(): void {
    items.value = []
  }

  function openPanel(): void {
    panelOpen.value = true
  }

  function closePanel(): void {
    panelOpen.value = false
  }

  function togglePanel(): void {
    panelOpen.value = !panelOpen.value
  }

  function openConfigModal(): void {
    configModalOpen.value = true
  }

  function closeConfigModal(): void {
    configModalOpen.value = false
  }

  return {
    items,
    panelOpen,
    configModalOpen,
    unreadCount,
    hasUnread,
    sorted,
    add,
    markRead,
    markAllRead,
    dismiss,
    clear,
    openPanel,
    closePanel,
    togglePanel,
    openConfigModal,
    closeConfigModal,
  }
})
